import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Play, Pause, Maximize, Captions } from "lucide-react";

const VIDEO_ID = "kpJm51QQ54g";

const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
};

const ensureYTApi = () =>
    new Promise((resolve) => {
        if (window.YT && window.YT.Player) return resolve();
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof prev === "function") prev();
            resolve();
        };
        if (!document.getElementById("yt-iframe-api")) {
            const s = document.createElement("script");
            s.id = "yt-iframe-api";
            s.src = "https://www.youtube.com/iframe_api";
            document.body.appendChild(s);
        }
    });

export const WelcomeVideoModal = ({ open, onOpenChange }) => {
    const hostRef = useRef(null);
    const wrapRef = useRef(null);
    const playerRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [cur, setCur] = useState(0);
    const [dur, setDur] = useState(0);
    const [ccOn, setCcOn] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        ensureYTApi().then(() => {
            if (cancelled || !hostRef.current) return;
            // YT replaces the target node with the iframe — use a throwaway
            // (non-React) child so React reconciliation is never disturbed.
            const el = document.createElement("div");
            el.style.width = "100%";
            el.style.height = "100%";
            hostRef.current.appendChild(el);
            playerRef.current = new window.YT.Player(el, {
                videoId: VIDEO_ID,
                playerVars: {
                    autoplay: 1, controls: 0, modestbranding: 1, rel: 0,
                    iv_load_policy: 3, fs: 0, disablekb: 1, playsinline: 1,
                    cc_load_policy: 0, hl: "es",
                },
                events: {
                    onReady: (e) => {
                        setReady(true);
                        setDur(e.target.getDuration());
                        try { e.target.unloadModule("captions"); e.target.unloadModule("cc"); } catch (_) { /* noop */ }
                        setCcOn(false);
                        e.target.playVideo();
                    },
                    onStateChange: (e) => {
                        const YT = window.YT;
                        if (e.data === YT.PlayerState.PLAYING) {
                            setPlaying(true);
                            setDur(e.target.getDuration());
                        } else if (e.data === YT.PlayerState.PAUSED) {
                            setPlaying(false);
                        } else if (e.data === YT.PlayerState.ENDED) {
                            setPlaying(false);
                            e.target.seekTo(0);
                            e.target.pauseVideo();
                        }
                    },
                },
            });
        });
        return () => {
            cancelled = true;
            if (playerRef.current && playerRef.current.destroy) {
                try { playerRef.current.destroy(); } catch (_) { /* noop */ }
            }
            playerRef.current = null;
            setReady(false); setPlaying(false); setCur(0); setDur(0);
        };
    }, [open]);

    useEffect(() => {
        if (!playing) return;
        const id = setInterval(() => {
            const p = playerRef.current;
            if (p && p.getCurrentTime) {
                setCur(p.getCurrentTime());
                const d = p.getDuration();
                if (d) setDur(d);
            }
        }, 250);
        return () => clearInterval(id);
    }, [playing]);

    const togglePlay = () => {
        const p = playerRef.current;
        if (!p) return;
        if (playing) p.pauseVideo(); else p.playVideo();
    };

    const seek = (e) => {
        e.stopPropagation();
        const p = playerRef.current;
        if (!p || !dur) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        const t = ratio * dur;
        p.seekTo(t, true);
        setCur(t);
    };

    const toggleCC = (e) => {
        if (e) e.stopPropagation();
        const p = playerRef.current;
        if (!p) return;
        try {
            if (ccOn) {
                p.setOption("captions", "track", {});
                p.unloadModule("captions");
                p.unloadModule("cc");
                setCcOn(false);
            } else {
                p.loadModule("captions");
                p.loadModule("cc");
                p.setOption("captions", "track", { languageCode: "es" });
                p.setOption("cc", "track", { languageCode: "es" });
                setCcOn(true);
            }
        } catch (_) { /* noop */ }
    };

    const toggleFullscreen = (e) => {
        if (e) e.stopPropagation();
        const el = wrapRef.current;
        if (!el) return;
        if (document.fullscreenElement) {
            document.exitFullscreen?.();
        } else {
            (el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen)?.call(el);
        }
    };

    const pct = dur ? (cur / dur) * 100 : 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-5xl w-[95vw] p-0 overflow-hidden gap-0 border-0 rounded-2xl bg-[hsl(var(--trulyn-navy))] text-white shadow-2xl ring-1 ring-white/10 [&>button]:text-white/70 [&>button]:hover:text-white [&>button]:transition-colors"
                data-testid="welcome-video-modal"
            >
                <DialogHeader className="px-7 pt-6 pb-4 text-left space-y-1">
                    <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-yellow-400">TRULY IA360</span>
                    <DialogTitle className="text-2xl font-bold text-white">Bienvenido a su portal</DialogTitle>
                    <p className="text-sm text-white/60">
                        Este breve video le muestra dónde encontrar cada cosa dentro de su portal.
                    </p>
                </DialogHeader>

                <div className="px-4 pb-4">
                    <div ref={wrapRef} className="relative w-full bg-black select-none rounded-xl overflow-hidden ring-1 ring-white/10 shadow-lg" style={{ aspectRatio: "16 / 9" }}>
                    {/* YouTube player (chrome hidden via controls=0) */}
                    <div ref={hostRef} className="absolute inset-0 w-full h-full pointer-events-none" data-testid="welcome-video-host" />

                    {/* Interaction layer — captures all pointer events so YouTube's
                        own overlays (title, logo, share, settings) never appear. */}
                    <div
                        className="absolute inset-0 z-10 flex items-center justify-center cursor-pointer group"
                        onClick={togglePlay}
                        data-testid="welcome-video-surface"
                    >
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                            className={`flex items-center justify-center w-16 h-16 rounded-full bg-black/55 text-white backdrop-blur-sm transition-all duration-200 hover:bg-black/70 hover:scale-105 ${playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}
                            aria-label={playing ? "Pausar" : "Reproducir"}
                            data-testid="welcome-video-play-btn"
                        >
                            {playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}
                        </button>
                    </div>

                    {/* Custom timeline */}
                    <div className="absolute bottom-0 left-0 right-0 z-20 px-3 pb-2 pt-6 bg-gradient-to-t from-black/60 to-transparent">
                        <div className="flex items-center gap-3">
                            <span className="text-[11px] text-white/90 tabular-nums w-9">{fmt(cur)}</span>
                            <div
                                className="relative flex-1 h-3 flex items-center cursor-pointer group/bar"
                                onClick={seek}
                                data-testid="welcome-video-timeline"
                            >
                                <div className="absolute left-0 right-0 h-1 rounded-full bg-white/30" />
                                <div className="absolute left-0 h-1 rounded-full bg-yellow-400" style={{ width: `${pct}%` }} />
                                <div className="absolute w-3 h-3 rounded-full bg-yellow-400 shadow -translate-x-1/2 opacity-0 group-hover/bar:opacity-100 transition-opacity" style={{ left: `${pct}%` }} />
                            </div>
                            <span className="text-[11px] text-white/90 tabular-nums w-9">{fmt(dur)}</span>
                            <button
                                type="button"
                                onClick={toggleCC}
                                className={`p-1 rounded transition-colors ${ccOn ? "text-yellow-400" : "text-white/80 hover:text-white"}`}
                                title={ccOn ? "Desactivar subtítulos" : "Activar subtítulos"}
                                aria-label="Subtítulos"
                                data-testid="welcome-video-cc-btn"
                            >
                                <Captions className="w-5 h-5" />
                            </button>
                            <button
                                type="button"
                                onClick={toggleFullscreen}
                                className="p-1 rounded text-white/80 hover:text-white transition-colors"
                                title="Pantalla completa"
                                aria-label="Pantalla completa"
                                data-testid="welcome-video-fs-btn"
                            >
                                <Maximize className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
                </div>

            </DialogContent>
        </Dialog>
    );
};
